import { Link } from "react-router-dom";
import { Shield, Lock, Bot, Server, Mail, AlertTriangle, KeyRound, FileText } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { usePageSEO } from "@/hooks/usePageSEO";

const LAST_UPDATED = "June 2, 2026";

const Security = () => {
  usePageSEO({
    title: "Security at ISO27001KIT - Data Handling and Account Security",
    description:
      "How ISO27001KIT handles your data, secures accounts, and uses AI. Honest overview with placeholders where independent attestations are not yet in place.",
    canonicalPath: "/security",
  });

  return (
    <div className="min-h-screen bg-background">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border py-16">
        <div className="absolute top-[-150px] left-[-80px] w-[420px] h-[420px] bg-accent/5 rounded-full blur-[120px] pointer-events-none" />
        <div className="container mx-auto px-4 relative z-10 max-w-4xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/10 text-accent text-xs font-medium mb-4">
            <Shield className="h-3.5 w-3.5" /> Security overview
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-foreground">
            Security at ISO27001KIT
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl">
            We help teams prepare for ISO 27001, so we take security of our own platform seriously.
            This page describes how we handle your data today, what we do not yet claim, and how to
            reach us with security questions.
          </p>
          <p className="text-xs text-muted-foreground mt-4">Last updated: {LAST_UPDATED}</p>
        </div>
      </section>

      <div className="container mx-auto px-4 py-12 max-w-4xl space-y-10">
        {/* Honest scope */}
        <Card className="border-warning/30 bg-warning/5">
          <CardHeader className="flex flex-row items-start gap-3 space-y-0">
            <AlertTriangle className="h-5 w-5 text-warning mt-1 shrink-0" />
            <div>
              <CardTitle className="text-lg">Honest scope</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground space-y-2">
            <p>
              ISO27001KIT is a working tool that helps you prepare for ISO 27001 certification.
              We are <strong>not</strong> ourselves ISO 27001, SOC 2, or HIPAA certified at this time,
              and we do not claim otherwise.
            </p>
            <p>
              The sections below describe controls that are actually in place. Where an item is a
              placeholder for future disclosure, we say so explicitly rather than overclaim.
            </p>
          </CardContent>
        </Card>

        {/* Data handling */}
        <section>
          <SectionHeading icon={Server} title="Data handling overview" />
          <Card>
            <CardContent className="pt-6 space-y-4 text-sm text-muted-foreground">
              <Item title="What you can put into the platform">
                Most users of ISO27001KIT work in our free tools, the Document Pack, and the Audit
                Room workspace. Inputs are typically ISMS metadata - scope statements, risks,
                control decisions, evidence titles and dates. We ask you not to upload sensitive
                personal data, regulated data, or confidential business information unless it is
                required for a feature you are using.
              </Item>
              <Item title="Where your data is stored">
                Application data is stored in our managed cloud backend, which is hosted on
                infrastructure provided by reputable third-party cloud providers. A full
                subprocessor list is published below.
              </Item>
              <Item title="Encryption in transit">
                Traffic between your browser and ISO27001KIT is served over HTTPS.
              </Item>
              <Item title="Backups and retention">
                Account and workspace data is retained for as long as your account is active.
                You can request deletion at any time through the Delete My Data control in the
                footer or by emailing security at the address below.
              </Item>
              <Item title="Logging">
                We keep metadata-only operational logs (timestamps, route, anonymised identifiers)
                to operate and debug the service. We do not log document contents in our
                application audit trails.
              </Item>
            </CardContent>
          </Card>
        </section>

        {/* Account security */}
        <section>
          <SectionHeading icon={KeyRound} title="Account security overview" />
          <Card>
            <CardContent className="pt-6 space-y-4 text-sm text-muted-foreground">
              <Item title="Authentication">
                Sign-in uses email and password, or Google sign-in. Passwords are never stored in
                plain text - authentication is handled by our managed identity provider.
              </Item>
              <Item title="Email verification">
                Email verification is required before using free tools and for any account-bound
                feature, to reduce abuse and accidental account collisions.
              </Item>
              <Item title="Session management">
                Sessions can be ended at any time by signing out. If you suspect your account is
                compromised, contact us so we can help you reset access.
              </Item>
              <Item title="Access to your workspace">
                Today, workspaces are scoped to the signed-in user. Team-based access controls
                and SSO are on the roadmap and are <em>not</em> a current feature.
              </Item>
            </CardContent>
          </Card>
        </section>

        {/* AI / data-use transparency */}
        <section>
          <SectionHeading icon={Bot} title="AI and data-use transparency" />
          <Card>
            <CardContent className="pt-6 space-y-4 text-sm text-muted-foreground">
              <Item title="What AI is used for">
                Some features use AI to draft text, explain controls, or suggest justifications.
                AI output is always a draft. A human should review and approve any AI-generated
                content before it becomes part of your ISMS.
              </Item>
              <Item title="What we send to AI providers">
                When you use an AI feature, the prompt you submit is sent to our AI provider in
                order to return a response. We do not enrich prompts with other workspace data
                unless that data is part of the prompt you submitted.
              </Item>
              <Item title="Training on your data">
                We do not sell your data. We do not knowingly use customer inputs to train
                third-party foundation models. Where a provider offers a no-training option, we
                rely on that setting.
              </Item>
              <Item title="Models and providers in use">
                The ISO 27001 AI Assistant currently uses Google Gemini models, accessed through
                the Lovable AI Gateway. We may add or change supported models over time; our
                Privacy Policy is updated when subprocessors change.
              </Item>
            </CardContent>
          </Card>
        </section>

        {/* Subprocessors */}
        <section>
          <SectionHeading icon={FileText} title="Subprocessors" />
          <Card>
            <CardContent className="pt-6 space-y-3 text-sm text-muted-foreground">
              <p>
                We use the following third-party providers to operate ISO27001KIT. The full
                description of each provider's role and what data is shared is in our{" "}
                <Link to="/privacy-policy" className="text-accent hover:underline">Privacy Policy</Link>.
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Lovable Cloud (Supabase infrastructure) - hosting, authentication, database, edge functions, file storage</li>
                <li>Lemon Squeezy - payment processing, subscription billing, merchant of record</li>
                <li>Lovable AI Gateway - routing for AI Assistant requests</li>
                <li>Google (Gemini models, via the Lovable AI Gateway) - AI Assistant responses</li>
                <li>SMTP email provider (Hostinger) - transactional emails</li>
                <li>Microsoft Clarity - sampled session analytics for usability research</li>
                <li>Google OAuth - optional sign-in with Google</li>
              </ul>
              <p>
                If you need this list for a vendor security review, email us at the address below
                and we can respond directly.
              </p>
            </CardContent>
          </Card>
        </section>

        {/* Reporting / contact */}
        <section>
          <SectionHeading icon={Mail} title="Reporting a security issue" />
          <Card>
            <CardContent className="pt-6 space-y-4 text-sm text-muted-foreground">
              <p>
                If you believe you have found a security vulnerability or have a security-related
                question, please contact us directly. We aim to acknowledge security reports within
                a few business days.
              </p>
              <div className="flex flex-wrap gap-3">
                <a
                  href="mailto:contact@iso27001kit.com"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-accent text-accent-foreground hover:bg-accent/90 text-sm font-medium"
                >
                  <Lock className="h-4 w-4" /> contact@iso27001kit.com
                </a>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-border hover:bg-muted text-sm font-medium"
                >
                  <Mail className="h-4 w-4" /> General contact
                </Link>
              </div>
              <p className="text-xs">
                Please do not include exploit details in unencrypted email when reporting a
                suspected vulnerability. Send a short summary first and we will arrange a secure
                channel.
              </p>
            </CardContent>
          </Card>
        </section>

        {/* Related */}
        <section className="rounded-xl border border-border bg-muted/30 p-6">
          <h2 className="text-lg font-semibold mb-3">Related policies</h2>
          <ul className="grid sm:grid-cols-2 gap-2 text-sm">
            <li><Link to="/privacy-policy" className="text-accent hover:underline">Privacy Policy</Link></li>
            <li><Link to="/terms-of-service" className="text-accent hover:underline">Terms of Service</Link></li>
            <li><Link to="/refund-policy" className="text-accent hover:underline">Refund Policy</Link></li>
            <li><Link to="/disclaimer" className="text-accent hover:underline">Disclaimer</Link></li>
          </ul>
        </section>
      </div>
    </div>
  );
};

function SectionHeading({
  icon: Icon,
  title,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
}) {
  return (
    <div className="flex items-center gap-3 mb-4">
      <div className="h-9 w-9 rounded-lg bg-accent/10 flex items-center justify-center">
        <Icon className="h-5 w-5 text-accent" />
      </div>
      <h2 className="text-xl font-bold">{title}</h2>
    </div>
  );
}

function Item({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="font-medium text-foreground mb-1">{title}</p>
      <p>{children}</p>
    </div>
  );
}

export default Security;